/**
 * Colors for the Konva canvas, on Plan View's plan tokens. Konva draws with
 * literal color strings, not CSS classes, so the canvas cannot read the
 * `--ds-*` tokens directly — they are mirrored here in one place instead of
 * scattered as magic hex values through every node.
 *
 * Each value names the token it mirrors, and `canvasColors.spec.ts` resolves
 * that token in `index.css` and fails when the two part company. That spec
 * exists because a comment saying "keep these in sync" did not keep them in
 * sync: the canvas once had a blue of its own, then kept an old blue after the
 * palette moved, so a selected object was outlined in a different blue from the
 * button that had just created it.
 *
 * These are the light values. A floor plan is drawn on paper — Plan View's
 * `plan-canvas` is the white of a surface — so the drawing keeps its light
 * ground in either theme, the way a printed plan would.
 */

/** `#rrggbb` → `rgba(r, g, b, a)`, so a soft fill cannot drift from its solid. */
function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16))
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(alpha)})`
}

/** `--ds-primary`: the selection is always the same forest as the buttons (`plan-selection`). */
const ACCENT = '#1f4a33'

/** The selection's wash, as `--ds-plan-selection-soft`: the accent at 10%. */
const ACCENT_SOFT_ALPHA = 0.1

/** `--ds-data-5` (teal): landmarks, apart from the selection's forest and the dimensions' blue. */
const LANDMARK = '#236a61'

export const CANVAS = {
  /** Selection outline, transform handles, active guides — `--ds-primary`. */
  accent: ACCENT,
  /** Translucent fill inside a selection box or a marquee. */
  accentSoft: withAlpha(ACCENT, ACCENT_SOFT_ALPHA),
  /** A landmark's outline and name, in Build — `--ds-data-5`. */
  landmark: LANDMARK,
  /** …and the wash inside the one selected — `--ds-plan-landmark-soft`. */
  landmarkSoft: withAlpha(LANDMARK, ACCENT_SOFT_ALPHA),
  /** The drawing ground, and the fill of furniture on it — `--ds-surface` (`plan-canvas`). */
  ground: '#ffffff',
  /** Walls, the room's edge and furniture outlines — `--ds-plan-wall`. */
  wall: '#10241a',
  /** Seats in furniture glyphs — `--ds-plan-seat` (moss, the mark's venue wave). */
  seat: '#3e6b47',
  /** The plan grid's dots — `--ds-plan-dot`. */
  dot: '#ddd5c2',
  /** Text on the plan — `--ds-ink`. */
  ink: '#10241a',
  /** Text on a piece pushed out of focus — `--ds-ink-subtle`. */
  inkSubtle: '#66736a',
  /** A piece pushed out of focus, filled — `--ds-surface-sunken`. */
  sunken: '#f6f2e8',
  /** …and outlined — `--ds-line-control`. */
  lineControl: '#d6ccb4',
  /** A zone vertex handle's edge — `--ds-line-strong`. */
  lineStrong: '#7c8a80',
  /** A note: gold, like everything that asks for a look — `--ds-hold-soft`. */
  holdSoft: '#f7edd2',
  /** …its edge and lines — `--ds-hold-ink`. */
  holdInk: '#7a5c0d',
  /** A piece taken off the event, still on the plan — `--ds-danger-ink`. */
  danger: '#a8241a',
  /** Dimensions and measured lines — `--ds-data-1`. */
  dimension: '#2a5f97',
  /** Snap guides while dragging — `--ds-data-6` (plum), apart from the selection's forest. */
  guide: '#7b4b8e',
  /** Client-brought furniture when highlighted: clay outline — `--ds-data-4`. Plan
   *  View singles client furniture out in clay everywhere. */
  client: '#8f4535',
  /** …and its fill — `--ds-data-4-soft`. */
  clientSoft: '#f6dfd9',
  /** The venue's own furniture when highlighted: moss outline — `--ds-data-2`. */
  venue: '#3e6b47',
  /** Default fills for a new zone. A zone keeps the fill it was saved with. */
  zone: {
    grass: '#d6e2cb',
    sand: '#efe7d4',
    water: '#cfe3e8',
    concrete: '#e7e4de',
    path: '#dccbb3',
    floor: '#eadcc1',
    structure: '#dedad3',
    tree: '#bcceaa',
    other: '#ebe8e3',
    /** The hairline around a zone, so two materials meet on a line — `--ds-zone-edge`. */
    edge: 'rgba(16, 36, 26, 0.14)',
  },
} as const

/** Which token each canvas color mirrors, for `canvasColors.spec.ts`. */
export const CANVAS_TOKENS: Record<string, string> = {
  accent: '--ds-primary',
  accentSoft: '--ds-plan-selection-soft',
  landmark: '--ds-data-5',
  landmarkSoft: '--ds-plan-landmark-soft',
  ground: '--ds-surface',
  wall: '--ds-plan-wall',
  seat: '--ds-plan-seat',
  dot: '--ds-plan-dot',
  ink: '--ds-ink',
  inkSubtle: '--ds-ink-subtle',
  sunken: '--ds-surface-sunken',
  lineControl: '--ds-line-control',
  lineStrong: '--ds-line-strong',
  holdSoft: '--ds-hold-soft',
  holdInk: '--ds-hold-ink',
  danger: '--ds-danger-ink',
  dimension: '--ds-data-1',
  guide: '--ds-data-6',
  client: '--ds-data-4',
  clientSoft: '--ds-data-4-soft',
  venue: '--ds-data-2',
  'zone.grass': '--ds-zone-grass',
  'zone.sand': '--ds-zone-sand',
  'zone.water': '--ds-zone-water',
  'zone.concrete': '--ds-zone-concrete',
  'zone.path': '--ds-zone-path',
  'zone.floor': '--ds-zone-floor',
  'zone.structure': '--ds-zone-structure',
  'zone.tree': '--ds-zone-tree',
  'zone.other': '--ds-zone-other',
  'zone.edge': '--ds-zone-edge',
}
